/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AvailableFeatureFlags } from '../models/AvailableFeatureFlags';
import type { AvailableRoles } from '../models/AvailableRoles';
import type { OrganizationCreateRequest } from '../models/OrganizationCreateRequest';
import type { OrganizationResponse } from '../models/OrganizationResponse';
import type { Role } from '../models/Role';
import type { SQLModelPage_OrganizationResponse_ } from '../models/SQLModelPage_OrganizationResponse_';
import type { SQLModelPage_RealAssetDeploymentResponse_ } from '../models/SQLModelPage_RealAssetDeploymentResponse_';
import type { SQLModelPage_User_ } from '../models/SQLModelPage_User_';
import type { User } from '../models/User';
import type { UserUpdate } from '../models/UserUpdate';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class UsersService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Users:Current User
     * @returns User Successful Response
     * @throws ApiError
     */
    public usersCurrentUser(): CancelablePromise<User> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/users/me',
            errors: {
                401: `Missing token or inactive user.`,
            },
        });
    }

    /**
     * Users:Patch Current User
     * @returns User Successful Response
     * @throws ApiError
     */
    public usersPatchCurrentUser({
        requestBody,
    }: {
        requestBody: UserUpdate,
    }): CancelablePromise<User> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/users/me',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `Missing token or inactive user.`,
                422: `Validation Error`,
            },
        });
    }

    /**
     * Users:User
     * @returns User Successful Response
     * @throws ApiError
     */
    public usersUser({
        id,
    }: {
        id: any,
    }): CancelablePromise<User> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/users/{id}',
            path: {
                'id': id,
            },
            errors: {
                401: `Missing token or inactive user.`,
                403: `Not a superuser.`,
                404: `The user does not exist.`,
                422: `Validation Error`,
            },
        });
    }

    /**
     * Users:Delete User
     * @returns void
     * @throws ApiError
     */
    public usersDeleteUser({
        id,
    }: {
        id: any,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/users/{id}',
            path: {
                'id': id,
            },
            errors: {
                401: `Missing token or inactive user.`,
                403: `Not a superuser.`,
                404: `The user does not exist.`,
                422: `Validation Error`,
            },
        });
    }

    /**
     * Users:Patch User
     * @returns User Successful Response
     * @throws ApiError
     */
    public usersPatchUser({
        id,
        requestBody,
    }: {
        id: any,
        requestBody: UserUpdate,
    }): CancelablePromise<User> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/users/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad Request`,
                401: `Missing token or inactive user.`,
                403: `Not a superuser.`,
                404: `The user does not exist.`,
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Users
     * @returns User Successful Response
     * @throws ApiError
     */
    public listUsers(): CancelablePromise<Array<User>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/users',
        });
    }

    /**
     * List Roles
     * @returns Role Successful Response
     * @throws ApiError
     */
    public listRoles(): CancelablePromise<Array<Role>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/users/roles/',
        });
    }

    /**
     * Set Roles For User
     * @returns any Successful Response
     * @throws ApiError
     */
    public setRolesForUser({
        userId,
        requestBody,
    }: {
        userId: string,
        requestBody: Array<AvailableRoles>,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/users/{user_id}/roles',
            path: {
                'user_id': userId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Feature Flags
     * @returns any Successful Response
     * @throws ApiError
     */
    public listFeatureFlags(): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/users/feature_flags/',
        });
    }

    /**
     * Set Feature Flags For User
     * @returns any Successful Response
     * @throws ApiError
     */
    public setFeatureFlagsForUser({
        userId,
        requestBody,
    }: {
        userId: string,
        requestBody: Array<AvailableFeatureFlags>,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/users/{user_id}/feature_flags',
            path: {
                'user_id': userId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Remove User From Organization
     * @returns any Successful Response
     * @throws ApiError
     */
    public removeUserFromOrganization({
        userId,
    }: {
        userId: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/users/{user_id}/organization',
            path: {
                'user_id': userId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Add User To Organization
     * @returns UserUpdate Successful Response
     * @throws ApiError
     */
    public addUserToOrganization({
        userId,
        organizationId,
    }: {
        userId: string,
        organizationId: string,
    }): CancelablePromise<UserUpdate> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/users/{user_id}/organization',
            path: {
                'user_id': userId,
            },
            query: {
                'organization_id': organizationId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Organizations
     * @returns SQLModelPage_OrganizationResponse_ Successful Response
     * @throws ApiError
     */
    public listOrganizations({
        page = 1,
        size = 50,
    }: {
        page?: number,
        size?: number,
    }): CancelablePromise<SQLModelPage_OrganizationResponse_> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/organization/',
            query: {
                'page': page,
                'size': size,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Organization
     * @returns OrganizationResponse Successful Response
     * @throws ApiError
     */
    public createOrganization({
        requestBody,
    }: {
        requestBody: OrganizationCreateRequest,
    }): CancelablePromise<OrganizationResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/organization/',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Organization Detail
     * @returns OrganizationResponse Successful Response
     * @throws ApiError
     */
    public getOrganizationDetail({
        organizationId,
    }: {
        organizationId: string,
    }): CancelablePromise<OrganizationResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/organization/{organization_id}',
            path: {
                'organization_id': organizationId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Organization
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteOrganization({
        organizationId,
    }: {
        organizationId: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/organization/{organization_id}',
            path: {
                'organization_id': organizationId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Set Roles For Organization
     * @returns OrganizationResponse Successful Response
     * @throws ApiError
     */
    public setRolesForOrganization({
        organizationId,
        requestBody,
    }: {
        organizationId: string,
        requestBody: Array<AvailableRoles>,
    }): CancelablePromise<OrganizationResponse> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/organization/{organization_id}/roles',
            path: {
                'organization_id': organizationId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Deployment Grants For Organization
     * @returns SQLModelPage_RealAssetDeploymentResponse_ Successful Response
     * @throws ApiError
     */
    public listDeploymentGrantsForOrganization({
        organizationId,
        page = 1,
        size = 50,
    }: {
        organizationId: string,
        page?: number,
        size?: number,
    }): CancelablePromise<SQLModelPage_RealAssetDeploymentResponse_> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/organization/{organization_id}/deployment_grants',
            path: {
                'organization_id': organizationId,
            },
            query: {
                'page': page,
                'size': size,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Bulk Add Deployment Grants For Organization
     * @returns any Successful Response
     * @throws ApiError
     */
    public bulkAddDeploymentGrantsForOrganization({
        organizationId,
        requestBody,
    }: {
        organizationId: string,
        requestBody: Array<string>,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/organization/{organization_id}/bulk_add_deployment_grants',
            path: {
                'organization_id': organizationId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Bulk Delete Deployment Grants For Organization
     * @returns any Successful Response
     * @throws ApiError
     */
    public bulkDeleteDeploymentGrantsForOrganization({
        organizationId,
        requestBody,
    }: {
        organizationId: string,
        requestBody: Array<string>,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/organization/{organization_id}/bulk_delete_deployment_grants',
            path: {
                'organization_id': organizationId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Organization Users
     * @returns SQLModelPage_User_ Successful Response
     * @throws ApiError
     */
    public listOrganizationUsers({
        organizationId,
        page = 1,
        size = 50,
    }: {
        organizationId: string,
        page?: number,
        size?: number,
    }): CancelablePromise<SQLModelPage_User_> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/organization/{organization_id}/users',
            path: {
                'organization_id': organizationId,
            },
            query: {
                'page': page,
                'size': size,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
